<template>
  <ErrorDialog
    v-model="showErrorDialog"
    :message="errorDialogMessage"
  />

  <div class="row justify-content-center">
    <div class="col-md-6 col-lg-5">
      <div class="reset-password-card">
        <h1 class="page-title">{{ $t('auth.resetPassword.title') }}</h1>

        <p class="description">
          {{ $t('auth.resetPassword.description1') }}<br>
          {{ $t('auth.resetPassword.description2') }}
        </p>

        <form @submit.prevent="submit" novalidate>
          <div class="form-group">
            <label for="email" class="form-label">{{ $t('auth.resetPassword.email') }}</label>
            <input
              type="email"
              id="email"
              class="form-control"
              :class="{ 'is-invalid': errors.email }"
              v-model="email"
              placeholder="example@town.higashikawa.lg.jp"
              :disabled="loading"
            >
            <div v-if="errors.email" class="invalid-feedback">
              {{ Array.isArray(errors.email) ? errors.email[0] : errors.email }}
            </div>
          </div>

          <div class="form-group">
            <label for="password" class="form-label">{{ $t('auth.resetPassword.password') }}</label>
            <div class="password-input-wrapper">
              <input
                :type="showPassword ? 'text' : 'password'"
                id="password"
                class="form-control"
                :class="{ 'is-invalid': errors.password }"
                v-model="password"
                :disabled="loading"
              >
              <button
                type="button"
                class="btn-password-toggle"
                @click="showPassword = !showPassword"
                tabindex="-1"
              >
                <i :class="showPassword ? 'bi bi-eye' : 'bi bi-eye-slash'"></i>
              </button>
            </div>
            <div v-if="errors.password" class="invalid-feedback">
              {{ Array.isArray(errors.password) ? errors.password[0] : errors.password }}
            </div>
          </div>

          <div class="form-group">
            <label for="passwordConfirm" class="form-label">{{ $t('auth.resetPassword.passwordConfirm') }}</label>
            <div class="password-input-wrapper">
              <input
                :type="showPasswordConfirm ? 'text' : 'password'"
                id="passwordConfirm"
                class="form-control"
                :class="{ 'is-invalid': errors.password_confirmation }"
                v-model="passwordConfirm"
                :disabled="loading"
              >
              <button
                type="button"
                class="btn-password-toggle"
                @click="showPasswordConfirm = !showPasswordConfirm"
                tabindex="-1"
              >
                <i :class="showPasswordConfirm ? 'bi bi-eye' : 'bi bi-eye-slash'"></i>
              </button>
            </div>
            <div v-if="errors.password_confirmation" class="invalid-feedback">
              {{ Array.isArray(errors.password_confirmation) ? errors.password_confirmation[0] : errors.password_confirmation }}
            </div>
          </div>

          <button
            type="submit"
            class="btn btn-primary btn-submit"
            :disabled="loading || !isFormValid"
          >
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ $t('auth.resetPassword.submit') }}
          </button>

          <button
            type="button"
            class="btn btn-secondary btn-back"
            @click="goToLogin"
            :disabled="loading"
          >
            {{ $t('auth.resetPassword.backToLogin') }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { resetPassword } from '../../../api/user/AuthApi';
import useFlashMessage from '../../../composables/useFlashMessage';
import { colors } from '../../../../consts/colors';
import { validatePasswordComplexity } from '../../../utils/passwordValidator';
import ErrorDialog from '../../../components/common/ErrorDialog.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { successMessage, errorMessage } = useFlashMessage();

const email = ref('');
const password = ref('');
const passwordConfirm = ref('');
const loading = ref(false);
const errors = ref({});
const showPassword = ref(false);
const showPasswordConfirm = ref(false);
const showErrorDialog = ref(false);
const errorDialogMessage = ref('');

/**
 * Check if form is valid
 */
const isFormValid = computed(() => {
  if (!email.value || !password.value || !passwordConfirm.value) {
    return false;
  }
  if (password.value !== passwordConfirm.value) {
    return false;
  }
  return true;
});

/**
 * Validate form on client side
 */
const validateForm = () => {
  errors.value = {};
  let isValid = true;

  // Email validation
  if (!email.value || email.value.trim() === '') {
    errors.value.email = t('auth.resetPassword.validation.emailRequired');
    isValid = false;
  } else if (!isValidEmail(email.value)) {
    errors.value.email = t('auth.resetPassword.validation.emailInvalid');
    isValid = false;
  }

  // Password validation
  if (!password.value || password.value.trim() === '') {
    errors.value.password = t('auth.resetPassword.validation.passwordRequired');
    isValid = false;
  } else {
    // Validate password complexity
    const complexityResult = validatePasswordComplexity(password.value);
    if (!complexityResult.isValid) {
      // Show the first error
      const firstError = complexityResult.errors[0];
      // Convert password_invalid_characters to invalidCharacters
      const errorSuffix = firstError.replace('password_', '').replace(/_([a-z])/g, (match, p1) => p1.toUpperCase());
      errors.value.password = t(`validation.password.${errorSuffix}`);
      isValid = false;
    }
  }

  // Password confirmation validation
  if (!passwordConfirm.value || passwordConfirm.value.trim() === '') {
    errors.value.password_confirmation = t('auth.resetPassword.validation.passwordConfirmRequired');
    isValid = false;
  } else if (password.value !== passwordConfirm.value) {
    errors.value.password_confirmation = t('auth.resetPassword.validation.passwordMismatch');
    isValid = false;
  }

  return isValid;
};

/**
 * Check email format
 */
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Submit password reset
 */
const submit = async () => {
  if (!validateForm()) {
    return;
  }

  loading.value = true;
  errors.value = {};

  try {
    await resetPassword({
      email: email.value,
      password: password.value,
      password_confirmation: passwordConfirm.value,
      token: route.params.token
    });

    successMessage(t('auth.resetPassword.success'));
    await router.push({ name: 'login' });
  } catch (error) {
    if (error.status === 422) {
      if (error.errors) {
        errors.value = error.errors;
      } else if (error.message) {
        errorMessage(error.message);
      }
    } else {
      errorMessage(t('auth.resetPassword.failed'));
    }
  } finally {
    loading.value = false;
  }
};

/**
 * Navigate to login page
 */
const goToLogin = () => {
  router.push({ name: 'login' });
};
</script>

<style lang="scss" scoped>
.reset-password-card {
  background-color: v-bind('colors.white');
  padding: 2.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px v-bind('colors.shadowColor');
}

.page-title {
  color: v-bind('colors.textPrimary');
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 1.5rem;
}

.description {
  color: v-bind('colors.textPrimary');
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
  margin-bottom: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;

  .form-label {
    color: v-bind('colors.textPrimary');
    font-weight: 500;
    margin-bottom: 0.5rem;
    display: block;
  }

  .form-control {
    width: 100%;
    border: 1px solid v-bind('colors.borderMediumGray');
    border-radius: 4px;
    padding: 0.75rem;
    color: v-bind('colors.textPrimary');

    &:focus {
      border-color: v-bind('colors.btnBgColorPrimary');
      box-shadow: 0 0 0 0.2rem rgba(48, 111, 201, 0.25);
      outline: none;
    }

    &.is-invalid {
      border-color: v-bind('colors.error');
    }
  }

  .invalid-feedback {
    color: v-bind('colors.error');
    font-size: 0.875rem;
    margin-top: 0.25rem;
    display: block;
  }
}

.password-input-wrapper {
  position: relative;

  .form-control {
    padding-right: 2.5rem;
  }
}

.btn-password-toggle {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: v-bind('colors.gray[600]');
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: v-bind('colors.gray[700]');
  }

  &:focus {
    outline: none;
  }

  i {
    font-size: 1.1rem;
  }
}

.btn {
  width: 100%;
  padding: 0.75rem;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-submit {
  background-color: v-bind('colors.btnBgColorPrimary');
  border-color: v-bind('colors.btnBgColorPrimary');
  color: v-bind('colors.white');
  margin-bottom: 1rem;
  border: none;

  &:hover:not(:disabled) {
    background-color: v-bind('colors.blue[600]');
    border-color: v-bind('colors.blue[600]');
  }

  &:active:not(:disabled) {
    background-color: v-bind('colors.blue[700]');
  }
}

.btn-back {
  background-color: v-bind('colors.white');
  color: v-bind('colors.btnBgColorPrimary');
  border: 1px solid v-bind('colors.borderMediumGray');

  &:hover:not(:disabled) {
    background-color: v-bind('colors.gray[50]');
  }

  &:active:not(:disabled) {
    background-color: v-bind('colors.gray[300]');
  }
}
</style>
